import { framework } from "@sablier/v2-contracts";
import { policy } from "@sablier/v2-machines";
import { _ } from "@sablier/v2-mixins";
import { vendors } from "@sablier/v2-utils";
import { isHostSafe } from "~/client/contexts/Web3";
import { useModalTransaction } from "~/client/hooks";
import { peripheral } from "~/client/utils";
import type { useMachineInstantiator } from "@sablier/v2-hooks";
import type { Translate } from "@sablier/v2-locales";
import type { IAddress, ISigner, IWagmiAddress } from "@sablier/v2-types";
import helper from "./helper";
import { plugin as wording } from "./wording";

export interface Payload {
  chainId: number;
  signer: ISigner;
  proxy: IAddress;
  api: {
    reset: () => void;
    setOpen: ReturnType<typeof useModalTransaction>["setOpen"];
    t: Translate;
    updateData: ReturnType<typeof useModalTransaction>["updateData"];
  };
}

type Machine = Parameters<typeof useMachineInstantiator<Payload>>;

type onCreate = Parameters<Machine["0"]["onCreate"]>["0"];

export async function onCreate({ context }: onCreate): Promise<void> {
  const { signer, chainId, api } = context.payload;
  const { t } = api;
  const registry = peripheral(chainId, "registry").address;
  const plugin = peripheral(chainId, "plugin").address;
  try {
    api.setOpen(true, {
      status: "confirm",
      title: wording.title(t),
      description: wording.send(t).description,
      isNotClosable: true,
    });

    const query = framework.contextualize(
      registry,
      chainId!,
      "registry",
      "installPlugin",
      [plugin as IWagmiAddress],
    );

    const config = await helper.configure({ chainId, query, signer });
    const transaction = await framework.write(config);

    api.updateData({
      status: "loading",
      description: wording.send(t).description,
      hash: !isHostSafe ? transaction.hash : undefined,
      isNotClosable: false,
    });

    const receipt = isHostSafe
      ? await framework.safeWait({ hash: transaction.hash })
      : await framework.wait({
          hash: transaction.hash,
          onReplaced: (replaced) => {
            api.updateData({
              hash: replaced.transaction.hash,
            });
          },
        });

    console.info("%c[post-transaction]", "color: mediumslateblue", {
      transaction,
      receipt,
    });

    if (receipt.status === "reverted") {
      throw new Error(policy.error.reverted(t));
    }

    api.updateData({
      status: "success",
      description: wording.success(t).description,
      hash: receipt.transactionHash,
      isNotClosable: false,
    });

    api.reset();
  } catch (error) {
    vendors.crash.log(error);
    api.updateData({
      status: "fail",
      description: wording.fail(t).description,
      error: {
        message: policy.error.message(t, error),
        data: error,
      },
      isNotClosable: false,
    });

    throw error;
  }
}
